// Land, Währung und Steuersätze des Betriebs.
//
// Das Profil bestimmt, welche Steuersätze in Belegen angeboten werden, in
// welcher Währung Beträge erscheinen und ob die deutschen Steuermodule
// (EÜR, Umsatzsteuer-Voranmeldung, DATEV) überhaupt sinnvoll sind.
import { useState } from 'react';
import { meldungZeigen } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, type Profil } from '../../../gemeinsam/lib/daten';
import { Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Land = {
  code: string; name: string; waehrung: string;
  steuersaetze: number[]; steuerName: string; deutschesSteuerrecht: boolean;
};

const zahlenListe = (text: string) => text
  .split(/[,;\s]+/)
  .map(t => Number(t.replace(',', '.')))
  .filter(n => Number.isFinite(n) && n >= 0 && n <= 100);

export function Laenderprofil({ profil, aktualisiert }: {
  profil: Profil;
  aktualisiert: () => void;
}) {
  const { daten: auswahl } = useDaten<{ laender: Land[]; waehrungen: string[] }>('/firma/laender');

  const [land, setLand] = useState(profil.land || profil.code || 'DE');
  const [waehrung, setWaehrung] = useState(profil.waehrung);
  const [saetze, setSaetze] = useState(profil.steuersaetze.join(', '));
  const [standard, setStandard] = useState(String(profil.standardSteuersatz));
  const [steuerName, setSteuerName] = useState(profil.steuerName);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  const gewaehlt = auswahl?.laender.find(l => l.code === land);
  const geprüft = zahlenListe(saetze);

  // Beim Landwechsel die Vorgaben des Landes übernehmen — der Anwender kann
  // sie danach überschreiben.
  function landWechseln(code: string) {
    setLand(code);
    const l = auswahl?.laender.find(x => x.code === code);
    if (l) {
      setWaehrung(l.waehrung);
      setSaetze(l.steuersaetze.join(', '));
      setStandard(String(l.steuersaetze[0]));
      setSteuerName(l.steuerName);
    }
  }

  async function speichern() {
    if (geprüft.length === 0) {
      setFehler('Bitte mindestens einen gültigen Steuersatz angeben (0 bis 100).');
      return;
    }
    setSendet(true);
    setFehler(null);
    try {
      await api('/firma/laenderprofil', {
        methode: 'PUT',
        daten: {
          land, waehrung, steuerName,
          steuersaetze: geprüft,
          standardSteuersatz: Number(standard.replace(',', '.')),
        },
      });
      // Die Seite lädt gleich neu — die Meldung muss das überstehen.
      meldungZeigen('Land, Währung und Steuersätze gespeichert.', { nachNeuladen: true });
      // Währung und Schreibweise stecken in vielen Anzeigen. Statt sie einzeln
      // nachzuziehen, wird einmal sauber neu geladen.
      setTimeout(() => window.location.reload(), 900);
      aktualisiert();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Konnte nicht gespeichert werden.');
    } finally { setSendet(false); }
  }

  if (!auswahl) return <Karte><div className="flex justify-center p-8"><Spinner /></div></Karte>;

  return (
    <Karte>
      <KartenKopf titel="Land, Währung und Steuersätze"
                  hinweis="Gilt für alle neuen Belege — bereits gebuchte bleiben unverändert" />
      <div className="space-y-4 p-5">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Auswahl label="Land" value={land} onChange={e => landWechseln(e.target.value)}>
            {auswahl.laender.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </Auswahl>
          <Auswahl label="Währung" value={waehrung} onChange={e => setWaehrung(e.target.value)}>
            {auswahl.waehrungen.map(w => <option key={w} value={w}>{w}</option>)}
          </Auswahl>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label="Steuersätze in Prozent" value={saetze}
                hinweis="Mehrere durch Komma trennen, z. B. 19, 7, 0"
                onChange={e => setSaetze(e.target.value)} />
          <Auswahl label="Standardsatz für neue Positionen" value={standard}
                   onChange={e => setStandard(e.target.value)}>
            {geprüft.map(s => <option key={s} value={String(s)}>{s} %</option>)}
          </Auswahl>
        </div>

        <Feld label="Bezeichnung der Steuer" value={steuerName}
              hinweis="Erscheint so auf jedem Beleg — z. B. Umsatzsteuer, TVA, 增值税"
              onChange={e => setSteuerName(e.target.value)} />

        {gewaehlt && !gewaehlt.deutschesSteuerrecht && (
          <Hinweis art="warnung" titel="Deutsche Steuermodule werden ausgeblendet">
            EÜR, Umsatzsteuer-Voranmeldung, DATEV-Export und das Fahrtenbuch folgen deutschem
            Steuerrecht. Für {gewaehlt.name} liefern sie keine verwertbaren Zahlen und
            verschwinden deshalb aus dem Menü. Lager, Kasse, Belege und Kunden bleiben
            vollständig nutzbar.
          </Hinweis>
        )}

        <div className="flex items-center gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <Knopf variante="primaer" laedt={sendet} onClick={speichern}>Speichern</Knopf>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Vorschau: {(1234.5).toLocaleString(profil.locale, { style: 'currency', currency: waehrung })}
          </span>
        </div>
      </div>
    </Karte>
  );
}
